import type { PermissionProps } from "@qendo/permissions";
import type {
    ClinicalDocumentData,
    DocumentPayload,
    MediaCreatePayload,
    MediaPayload,
    MediaUpdatePayload,
    PhotographData,
} from "@qendo/profile/files";
import type { ListData } from "@qendo/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";
import type { File } from "expo-file-system";
import { createContext, type ReactNode, useCallback, useContext, useMemo } from "react";
import { resolveStoreMethod, type UploadResponse, useApi } from "~/providers/ApiProvider";
import { empty, filled } from "~/util";
import { deleteQuery, documentFile, mergeQuery, removeQuery } from "~/util/api";
import { isMediaCreatePayload } from "~/util/qendo/media";

declare interface GalleryState {
    documents: ClinicalDocumentData[];
    pain_plan: ClinicalDocumentData[];
    media: PhotographData[];
    refreshDocuments(): Promise<void>;
    refreshMedia(): Promise<void>;
    findDocument(id: string, pain?: boolean): ClinicalDocumentData | null;
    findMedia(id: string): PhotographData | null;
    storeDocument(payload: DocumentPayload): Promise<string | null>;
    destroyDocument(id: string): Promise<void>;
    downloadDocument(id?: string, file?: string, overwrite?: boolean): Promise<File | null>;
    canDestroyMedia(id: string): boolean;
    storeMedia(payload: MediaPayload): Promise<string | null>;
    destroyMedia(id: string): Promise<void>;
}

export const GalleryContext = createContext<GalleryState>({
    documents: [],
    pain_plan: [],
    media: [],
    refreshDocuments: async () => {
        return;
    },
    refreshMedia: async () => {
        return;
    },
    findDocument: (_id, _pain) => null,
    findMedia: (_id) => null,
    storeDocument: async (_payload) => null,
    destroyDocument: async (_id) => {
        return;
    },
    downloadDocument: async (_id, _file, _overwrite) => null,
    canDestroyMedia: (_id) => false,
    storeMedia: async (_payload) => null,
    destroyMedia: async (_id) => {
        return;
    },
});

export function useGallery() {
    const value = useContext(GalleryContext);
    if (!value) {
        throw new Error("useGallery must be wrapped in a <GalleryProvider />");
    }
    return value;
}

export type GalleryProviderProps = {
    profile_id: string | null;
    children: ReactNode;
} & PermissionProps;

export function GalleryProvider({ profile_id, can, cannot, children }: GalleryProviderProps) {
    const queryClient = useQueryClient();
    const { client, upload, download } = useApi();

    const documentsKey = ["documents", profile_id];
    const galleryKey = ["gallery", profile_id];

    const refreshDocuments = async () => {
        await queryClient.invalidateQueries({ queryKey: documentsKey, refetchType: "all" });
    };
    const refreshMedia = async () => {
        await queryClient.invalidateQueries({ queryKey: galleryKey, refetchType: "all" });
    };

    const documentsQuery = useQuery({
        queryKey: documentsKey,
        queryFn: async () => {
            if (cannot("view", "documents")) {
                return [];
            }

            return await client()
                .get(`profiles/${profile_id}/documents`)
                .then((response: AxiosResponse<ListData<ClinicalDocumentData>>) => {
                    return response.data.data;
                })
                .catch(() => []);
        },
        staleTime: 1000 * 60 * 60, // 1 hour
        placeholderData: [],
    });

    const documents: ClinicalDocumentData[] = useMemo(() => documentsQuery.data ?? [], [documentsQuery]);

    const pain_plan: ClinicalDocumentData[] = useMemo(
        () => (filled(documents) ? documents.filter(({ pain_plan }) => pain_plan) : []),
        [documents],
    );

    const findDocument = useCallback(
        (id: string, pain?: boolean) => documents.find((p) => p.id === id && (!pain || p.pain_plan)) ?? null,
        [documents],
    );

    const downloadDocument = async (id: string, file: string, overwrite?: boolean) => {
        const document = findDocument(id);
        const target = document?.files?.find(({ id }) => id === file);

        if (!target) {
            return null;
        }

        const local = documentFile(target.filename);

        return local.exists && !overwrite
            ? local
            : await download(target.url, target.filename, overwrite).then((downloaded) =>
                  downloaded?.exists ? downloaded : null,
              );
    };

    const storeDocumentMutation = useMutation({
        mutationFn: async ({ id, assets, ...payload }: DocumentPayload) => {
            const files = await Promise.allSettled(
                assets.map((asset) => upload(profile_id as string, "documents", asset)),
            ).then((results) =>
                results
                    .filter((result): result is PromiseFulfilledResult<UploadResponse> => result.status === "fulfilled")
                    .map(({ value }) => value),
            );

            const { method, url } = resolveStoreMethod(`profiles/${profile_id}/documents`, id);

            return await client()
                [method](url, {
                    files,
                    ...payload,
                })
                .then(({ data }) => data);
        },
        onSuccess: (doc: ClinicalDocumentData) => {
            queryClient.setQueryData(documentsKey, mergeQuery(doc));
        },
    });

    const storeDocument = async (payload: DocumentPayload) => {
        if (cannot("update", "documents")) {
            return null;
        }

        return await storeDocumentMutation.mutateAsync(payload).then(({ id }) => id);
    };

    const destroyDocumentMutation = useMutation({
        mutationFn: async (id: string) => {
            return await deleteQuery(client(), `profiles/${profile_id}/documents/${id}`, id);
        },
        onSuccess: (id: string) => {
            queryClient.setQueryData(documentsKey, removeQuery(id));
        },
    });

    const destroyDocument = async (id: string) => {
        if (cannot("update", "documents")) {
            return;
        }

        await destroyDocumentMutation.mutateAsync(id);
    };

    const mediaQuery = useQuery({
        queryKey: galleryKey,
        queryFn: async () => {
            if (cannot("view", "photos")) {
                return [];
            }

            return await client()
                .get(`profiles/${profile_id}/photographs`)
                .then((response: AxiosResponse<ListData<PhotographData>>) => {
                    return response.data.data;
                })
                .catch(() => []);
        },
        staleTime: 1000 * 60 * 60, // 1 hour
    });

    const media: PhotographData[] = useMemo(() => mediaQuery.data ?? [], [mediaQuery]);

    const findMedia = useCallback((id: string) => media.find((p) => p.id === id) ?? null, [media]);

    const canDestroyMedia = useCallback(
        (id: string) => can("update", "photos") && empty(findMedia(id)?.attachments),
        [can, findMedia],
    );

    const createMediaMutation = useMutation({
        mutationFn: async ({ asset, name }: MediaCreatePayload) => {
            const file = await upload(profile_id as string, "photographs", asset);

            return await client()
                .post(`profiles/${profile_id}/photographs`, {
                    name,
                    ...file,
                })
                .then(({ data }: AxiosResponse<PhotographData>) => data);
        },
        onSuccess: (image: PhotographData) => {
            queryClient.setQueryData(galleryKey, mergeQuery(image));
        },
    });

    const updateMediaMutation = useMutation({
        mutationFn: async ({ id, name }: MediaUpdatePayload) => {
            return await client()
                .patch(`profiles/${profile_id}/photographs/${id}`, { name })
                .then(({ data }: AxiosResponse<PhotographData>) => data);
        },
        onSuccess: (image: PhotographData) => {
            queryClient.setQueryData(galleryKey, mergeQuery(image));
        },
    });

    const storeMedia = async (payload: MediaPayload) => {
        if (cannot("update", "photos")) {
            return null;
        }

        return await (isMediaCreatePayload(payload)
            ? createMediaMutation.mutateAsync(payload)
            : updateMediaMutation.mutateAsync(payload)
        ).then(({ id }) => id);
    };

    const destroyMediaMutation = useMutation({
        mutationFn: async (id: string) => {
            return await deleteQuery(client(), `profiles/${profile_id}/photographs/${id}`, id);
        },
        onSuccess: (id: string) => {
            queryClient.setQueryData(galleryKey, removeQuery(id));
        },
    });

    const destroyMedia = async (id: string) => {
        if (!canDestroyMedia(id)) {
            return;
        }

        await destroyMediaMutation.mutateAsync(id);
    };

    const value: GalleryState = {
        media,
        documents,
        pain_plan,
        refreshDocuments,
        refreshMedia,
        findDocument,
        findMedia,
        storeDocument,
        destroyDocument,
        downloadDocument,
        canDestroyMedia,
        storeMedia,
        destroyMedia,
    };

    return <GalleryContext.Provider value={value}>{children}</GalleryContext.Provider>;
}
